import type {ReactElement} from 'react';
import {useRef} from 'react';
import React from 'react';
import {Text, TextSize, TextType} from '@Components/text';
import {FlatIconColor, FlatIconColorType, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {SelectableAiThumbnailItem} from '@Components/selectable-ai-thumbnail-item/selectable-ai-thumbnail-item';
import AudioItemWave from '@Components/base-grid/components/audio-item-wave/audio-item-wave';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import {useAiTranscriptGeneration} from '@Hooks/poster-editor/useAiTranscriptGeneration';
import type {SelectableItemWithAudio} from '@Panels/ai-subtitle-panel/ai-subtitle-panel.types';
import {AiTranscriptAction} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {ElementData} from '@Libraries/add-media-library';
import {setAreUploadsBeingProcessed} from '@Panels/ai-subtitle-panel/ai-subtitle-reducer';
import {useDispatch} from 'react-redux';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import type {AiSubtitleChooseMediaProps} from './ai-subtitle-choose-media.types';
import styles from './ai-subtitle-choose-media.module.scss';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {showAiSubtitlesUpsellDialog} from '@Libraries/ai-transcript.library';

const WAVE_OVERLAY = (
  <div className="_full-width _full-height flex-center">
    <AudioItemWave isAnimating={false} width={36} height={16} color={PMW_COLORS_NEUTRAL.NEUTRAL_0} />
  </div>
);

export function AiSubtitleChooseMedia({forceHorizontalScrollOnOverflow = false, areUploadsBeingProcessed = false, ...props}: AiSubtitleChooseMediaProps): ReactElement<any> {
  const dispatch = useDispatch();
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());

  const {topShadow} = useTopBottomScrollShadow(props.scrollableContainerRef);

  const {getAiSubtitlesActionForItem} = useAiTranscriptGeneration();

  const doesAiTranscriptExistForPlayableItem = (item: SelectableItemWithAudio): boolean => {
    if (!item.sourceProperties) {
      return false;
    }

    const action = getAiSubtitlesActionForItem(item.sourceProperties);

    return action === AiTranscriptAction.GENERATED;
  };

  const getSelectableItems = (): SelectableItemWithAudio[] => {
    const selectableItems: SelectableItemWithAudio[] = [];

    props.items.forEach((item) => {
      if (!doesAiTranscriptExistForPlayableItem(item)) {
        selectableItems.push(item);
      }
    });

    return selectableItems;
  };

  const shouldShowDeselectAll = (): boolean => {
    const selectedItems = props.items.filter((item) => {
      return item.isSelected && !doesAiTranscriptExistForPlayableItem(item);
    });

    return selectedItems.length > 0 && selectedItems.length === getSelectableItems().length;
  };

  const getClassesForSelectAllButton = (): string => {
    if (shouldShowDeselectAll()) {
      return 'content-primary _cursor-pointer';
    }

    if (getSelectableItems().length === 0) {
      return 'content-disabled _cursor-not-allowed';
    }

    return 'content-primary _cursor-pointer';
  };

  const getTextContainer = (): ReactElement<any> => {
    return (
      <div className={`_full-width _full-height spacing-p-b-3 ${topShadow ? styles.headerBottomShadow : styles.transparentBottomBorder}`}>
        <div className={`flex-v-row spacing-m-l-4 spacing-m-r-4 ${styles.textContainer}`}>
          <Text disableSelection type={TextType.BODY} size={TextSize.XSMALL} bold val={window.i18next.t('pmwjs_choose_audio_or_video')} className="content-body" />
          <Text disableSelection type={TextType.BODY} size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_select_for_subtitles')} className="content-sub-text" />
          <Text
            disableSelection
            type={TextType.BODY}
            size={TextSize.XXSMALL}
            bold
            val={shouldShowDeselectAll() ? window.i18next.t('pmwjs_deselect_all') : window.i18next.t('pmwjs_select_all')}
            className={`${styles.selectAllText} ${getClassesForSelectAllButton()}`}
            onClick={() => {
              if (shouldShowDeselectAll()) {
                props.onDeselectAll();
              } else {
                props.onSelectAll();
              }
            }}
          />
        </div>
      </div>
    );
  };

  const getUploadMediaItem = (): ReactElement<any> => {
    return (
      <li
        key="upload-media-btn"
        onClick={onUploadClicked}
        className={`${styles.uploadMediaContainer} ${areUploadsBeingProcessed ? styles.disabled : ''} hover-transition-all flex-shrink-0 radius-8 flex-column-align-center _cursor-pointer spacing-p-0`}
      >
        <div className={`${styles.uploadMedia} flex-h-row flex-center radius-8`}>
          <Icon
            isDisabled={areUploadsBeingProcessed}
            isLoading={areUploadsBeingProcessed}
            icon="icon-upload"
            size={IconSize.SIZE_ICON_24}
            type={IconType.FLAT}
            flatIconType={{color: FlatIconColor.NEUTRAL, type: FlatIconColorType.DEFAULT}}
          />
        </div>
        <Text className={styles.uploadText} val={areUploadsBeingProcessed ? window.i18next.t('pmwjs_uploading') : window.i18next.t('pmwjs_uploads')} size={TextSize.XXSMALL} />
      </li>
    );
  };

  const onAddSelected = async (items: ElementData[], currentUploadIndex?: number, totalUploadsCount?: number): Promise<void> => {
    await props.onItemAddedViaUploads(items, currentUploadIndex, totalUploadsCount);

    if (currentUploadIndex === totalUploadsCount) {
      dispatch(setAreUploadsBeingProcessed(false));
    }
  };

  const onUploadClicked = (): void => {
    if (areUploadsBeingProcessed) {
      return;
    }

    openUserMediaModal({
      onStartAddSelected: () => {
        dispatch(setAreUploadsBeingProcessed(true));
      },
      onUploadError: (_file, currentUploadIndex, totalItemsToUpload) => {
        if (currentUploadIndex === totalItemsToUpload) {
          dispatch(setAreUploadsBeingProcessed(false));
        }
      },
      uploadAcceptedMimetype: 'audio/mp3,audio/*,video/*',
      onAddSelected,
      mediaTypesToShow: [UploadsMediaType.AUDIO, UploadsMediaType.VIDEO],
      showVectors: false,
      defaultTab: UploadsMediaType.VIDEO,
    });
  };

  const getPlayableMediaItems = (): ReactElement<any> => {
    return (
      <>
        {props.items.map((item) => {
          return (
            <SelectableAiThumbnailItem
              isDisabled={doesAiTranscriptExistForPlayableItem(item)}
              key={item.id}
              overlayItem={item.type === 'audio' ? WAVE_OVERLAY : undefined}
              overlayText={`${Math.ceil(item.duration)}s`}
              id={item.id}
              text={item.name}
              isSelected={item.isSelected}
              thumbnailImage={item.imageUrl}
              onClick={(_, event) => {
                props.onItemClick(item.id, event);
              }}
            />
          );
        })}
      </>
    );
  };

  const getPlayableMediaGrid = (): ReactElement<any> => {
    return (
      <ul className={`spacing-p-l-4 spacing-p-r-4 spacing-m-0 flexbox ${forceHorizontalScrollOnOverflow ? styles.horizontalScroll : 'flex-wrap'} ${styles.playableMediaContainer}`}>
        {getUploadMediaItem()}
        {getPlayableMediaItems()}
      </ul>
    );
  };

  const getPremiumFeatureBannerForPAYGUsers = (): ReactElement<any> => {
    return (
      <div className={`${styles.premiumBannerBackground} radius-8 spacing-m-l-4 spacing-m-r-4 spacing-m-b-4 spacing-p-l-3 spacing-p-r-3 spacing-p-b-2 spacing-p-t-2`}>
        <Text type={TextType.BODY} size={TextSize.XXSMALL} className={'content-premium'} val={`${window.i18next.t('pmwjs_ai_subtitles_payg_description_part_1')} `} />
        <Text
          onClick={() => {
            void showAiSubtitlesUpsellDialog();
          }}
          underline
          type={TextType.BODY}
          size={TextSize.XXSMALL}
          className={`content-premium _cursor-pointer ${styles.upgradeText}`}
          val={window.i18next.t('pmwjs_upgrade')}
        />
        <Text type={TextType.BODY} size={TextSize.XXSMALL} className={'content-premium'} val={` ${window.i18next.t('pmwjs_ai_subtitles_payg_description_part_2')}`} />
      </div>
    );
  };

  return (
    <div className={`flex-v-row ${props.className ?? props.className}`}>
      {!isPremium.current ? getPremiumFeatureBannerForPAYGUsers() : null}
      {getTextContainer()}
      <div ref={props.scrollableContainerRef} className={`_full-width _full-height ${styles.listWrapper} ${styles.listWrapperMaxHeight}`}>
        {getPlayableMediaGrid()}
      </div>
    </div>
  );
}
